// Turns the `?error=` code the GBP connect callback redirects with into
// something a business owner can act on (design §7.2).
//
// Until this existed the callback carefully built an error redirect for every
// failure and the screen never read the query string, so a failed connect was
// pixel-identical to never having tried — most damagingly for the single most
// common real-world failure, authorizing with a Google account that does not
// manage the business. The customer would retry the same wrong account
// forever.
//
// Voice rules (design brief §3, CLAUDE.md): plain, specific, no error codes
// shown to the customer, no blame, and never a claim we cannot back — where
// we genuinely do not know whose side the failure was on, the copy says so
// from our side rather than guessing.
//
// The codes are produced by apps/dashboard/src/routes/auth/gbp.ts. Some of
// them ('gbp_invalid_challenge', 'gbp_state_mismatch', 'gbp_org_mismatch',
// 'gbp_unavailable') redirect to /app rather than here; they are mapped
// anyway so a hand-edited or stale URL still says something true.
//
// The four `gsc_*` codes below are the Search Console flow's twins of those
// four, and they are NOT decoration: /app is where they actually land, and
// `knownGbpConnectError` returns null (no banner, silent redirect to the
// overview) for anything this table does not carry.

import { TERMS } from '@pageboss/shared';

export interface GbpConnectError {
  message: string;
  /** Offer [Try another account] — the flow restarts with prompt=select_account. */
  canSwitchAccount: boolean;
}

const MESSAGES: Record<string, GbpConnectError> = {
  // §7.2's diagnosis, and the ONLY code that earns [Try another account].
  // The callback emits it strictly when every Google account answered and
  // every one of them was empty (routes/auth/gbp.ts) — a per-account listing
  // FAILURE is reported under its own code instead, precisely so this
  // sentence is never said to someone holding the right credentials.
  gbp_no_locations: {
    message:
      "We couldn't find a business on that Google account. Most likely you signed in with a different Google account than the one that manages your listing.",
    canSwitchAccount: true,
  },
  // These two used to offer [Try another account] as well. They must not:
  // classifyStatus (packages/gbp/src/client.ts) maps by HTTP status alone and
  // never reads the reason, so a 403 from accounts.list is just as likely to
  // be OUR project losing API enablement — which would hit every customer at
  // once — as anything about their account. Telling them to cycle Google
  // accounts through a pageboss outage would waste their afternoon and blame
  // them for it. Speak from our side and say what we actually know.
  gbp_forbidden: {
    message:
      "Google wouldn't let us read a Business Profile with that sign-in. If you manage your listing from a different Google account, that is worth trying — otherwise this is on our side and we're better placed to fix it than you are.",
    canSwitchAccount: false,
  },
  gbp_not_found: {
    message:
      "Google didn't return a business account for that sign-in. Nothing was saved — please try again, and let us know if it keeps happening.",
    canSwitchAccount: false,
  },
  gbp_unauthorized: {
    message:
      "Google didn't grant us access to your Business Profile. Try connecting again, and allow access when Google asks.",
    canSwitchAccount: false,
  },
  gbp_exchange: {
    message:
      "Google didn't complete the sign-in. Nothing was saved — connecting again usually clears it.",
    canSwitchAccount: false,
  },
  gbp_network: {
    message: "We couldn't reach Google just then. Nothing was saved — please try again.",
    canSwitchAccount: false,
  },
  gbp_quota: {
    message:
      'Google is limiting how often we can call it right now. Wait a few minutes and try connecting again.',
    canSwitchAccount: false,
  },
  gbp_upstream: {
    message:
      'Google had a problem on their side. Nothing was saved — please try again in a few minutes.',
    canSwitchAccount: false,
  },
  // Written by the callback's auto-match collision check. Same split as the
  // picker's 409: a same-org hit may say it is one of your own sites, a
  // cross-org hit says nothing that would identify another customer.
  gbp_already_connected: {
    message:
      'That Google business is already connected to another one of your sites. Disconnect it there first, then connect it here.',
    canSwitchAccount: false,
  },
  gbp_already_connected_elsewhere: {
    message:
      'That Google business is already connected to another pageboss account. Contact support if you think that is wrong.',
    canSwitchAccount: false,
  },
  // Written by the callback's ONE-CURRENT-BINDING check (Phase 2 gate remediation):
  // the Location already holds a DIFFERENT Google listing. Nothing was replaced or
  // disconnected; the owner disconnects first. The picker's 409 carries the same
  // sentence in its body.
  gbp_location_already_connected: {
    message:
      `This location is already connected to a ${TERMS.googleProfile}. Disconnect it first to connect a different one.`,
    canSwitchAccount: false,
  },
  // Written when a SECOND consent round-trip for the SAME listing lost a race with the first: the
  // Location is connected exactly as asked; only this later credential was not stored.
  gbp_connected_concurrently: {
    message:
      `This location was just connected to that same ${TERMS.googleProfile} from another window — nothing to redo.`,
    canSwitchAccount: false,
  },
  // Written by the callback's current-authority re-check (`gbp:manage` is owner
  // only — P2.3 policy row, enforced in production since the 2026-09-12 hotfix):
  // the signed-in member may not connect or change the Google connection — an
  // owner demoted while Google's consent screen was open, most likely. Distinct
  // from gbp_forbidden, which is Google's own 403. Lands on the site's Google
  // Profile screen.
  gbp_not_owner: {
    message:
      `Only the business owner can connect or change the ${TERMS.googleProfile}. Nothing was changed — ask the owner to connect it.`,
    canSwitchAccount: false,
  },
  tier: {
    message: `Connecting a ${TERMS.googleProfile} is part of Starter and up.`,
    canSwitchAccount: false,
  },
  // Deliberately cause-agnostic. This one code covers a missing `code`, a
  // missing `state`, a missing cookie and an expired challenge row — which
  // includes the customer simply pressing Cancel on Google's consent screen.
  // Asserting "it expired" would be a guess, and wrong most of the time.
  // NOTE on wording: these three land on /app (the sites list), not on the
  // site's GBP screen — the callback has no trustworthy site id to return to
  // by the time they fire. So none of them may say "start again from this
  // screen"; they have to point the customer back at the site.
  gbp_invalid_challenge: {
    message:
      "We didn't get a finished connection back from Google. Open the site you were connecting and try again.",
    canSwitchAccount: false,
  },
  gbp_state_mismatch: {
    message:
      "That connection attempt didn't check out, so we stopped it. Open the site you were connecting and start again.",
    canSwitchAccount: false,
  },
  gbp_org_mismatch: {
    message:
      'That connection attempt was started from a different pageboss account than the one signed in now. Open the site you were connecting and start again.',
    canSwitchAccount: false,
  },
  gbp_unavailable: {
    message: `Connecting a ${TERMS.googleProfile} isn't switched on right now.`,
    canSwitchAccount: false,
  },
  // --- Search Console (routes/auth/gsc.ts) ---------------------------------
  //
  // These four land HERE, on /app, and nowhere else: by the time they fire the
  // callback has no trustworthy site id to return to (the challenge is what
  // carries it, and three of the four are that challenge failing). Before this
  // they were unmapped, so `knownGbpConnectError` returned null, `hasError`
  // stayed false, and the owner was silently redirected to their overview as
  // if nothing had happened — most commonly by simply pausing 11 minutes on
  // Google's consent screen (CHALLENGE_TTL_MS is 10). The wording is the
  // Search Console twin of the GBP copy above, and for the same reason it must
  // point the customer back at the site rather than saying "from this screen".
  // The site's own screen has its own copy in components/gsc/state-copy.ts.
  gsc_invalid_challenge: {
    message:
      "We didn't get a finished connection back from Google. Open the site you were connecting and try again.",
    canSwitchAccount: false,
  },
  gsc_state_mismatch: {
    message:
      "That connection attempt didn't check out, so we stopped it. Open the site you were connecting and start again.",
    canSwitchAccount: false,
  },
  gsc_org_mismatch: {
    message:
      'That connection attempt was started from a different pageboss account than the one signed in now. Open the site you were connecting and start again.',
    canSwitchAccount: false,
  },
  gsc_unavailable: {
    message: "Connecting Search Console isn't switched on right now.",
    canSwitchAccount: false,
  },
  missing_site: {
    message: "We couldn't tell which site you were connecting, so nothing was started.",
    canSwitchAccount: false,
  },
  not_found: {
    message: "We couldn't find that site, so the connection didn't start.",
    canSwitchAccount: false,
  },
};

const UNKNOWN: GbpConnectError = {
  message: "We couldn't finish connecting to Google. Nothing was saved — please try again.",
  canSwitchAccount: false,
};

/**
 * Recognised codes ONLY — `null` for anything not in the table.
 *
 * Use this on any screen that is not exclusively the GBP flow's landing
 * place. `/app` is one: the callback sends several failures there, but so
 * does the demo-claim flow (routes/claim-finish.ts emits `claim_not_found`,
 * `demo_already_claimed`, `demo_unavailable`, `claim_email_mismatch` at the
 * same `?error=` key). Falling back to UNKNOWN there would tell a customer
 * whose demo claim failed that we "couldn't finish connecting to Google" —
 * a confident statement about something that never happened.
 */
export function knownGbpConnectError(code: string | null): GbpConnectError | null {
  if (!code) return null;
  return Object.hasOwn(MESSAGES, code) ? (MESSAGES[code] as GbpConnectError) : null;
}

/**
 * `null` for an absent code; an honest generic for one we don't recognise.
 * Safe only where every `?error=` value provably originates in the GBP
 * connect flow — today that's `/app/sites/:id/gbp` and the wizard's
 * `/app/b/:id/setup` (WP3 Task 9), both reached only via `gbpReturnPath`
 * (`apps/dashboard/src/lib/gbp-return-path.ts`), whose only writer is the
 * callback (`apps/dashboard/src/routes/auth/gbp.ts`). Any other screen would
 * need the same guarantee before reusing this function.
 */
export function gbpConnectError(code: string | null): GbpConnectError | null {
  if (!code) return null;
  // Object.hasOwn, NOT `MESSAGES[code] ?? UNKNOWN`. The code comes straight
  // off the query string, and a plain object literal inherits from
  // Object.prototype: `?error=toString` (or valueOf, constructor, __proto__)
  // would return an inherited value that is truthy — so the `??` fallback
  // never fires — whose `.message` is undefined. The screen would then render
  // an empty warning banner: a blank bar that says nothing and that assistive
  // tech announces as nothing, which is exactly what this module's header
  // promises can never happen.
  return Object.hasOwn(MESSAGES, code) ? (MESSAGES[code] as GbpConnectError) : UNKNOWN;
}
